import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  KeyRound, Search, Eye, EyeOff, Copy, RefreshCw, Download, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminCredentials() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [resetLoading, setResetLoading] = useState({});
  const [resetAllLoading, setResetAllLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API_URL}/drivers/credentials`);
      setDrivers(response.data);
    } catch (error) {
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (driverId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [driverId]: !prev[driverId]
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleResetPassword = async (driver) => {
    setResetLoading(prev => ({ ...prev, [driver.id]: true }));
    try {
      const response = await axios.post(`${API_URL}/drivers/${driver.id}/reset-password`);
      setNewPassword(response.data.temporary_password);
      setSelectedDriver(driver);
      setShowResetDialog(true);
      fetchDrivers(); // Refresh to get new password
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResetLoading(prev => ({ ...prev, [driver.id]: false }));
    }
  };

  const handleResetAllPasswords = async () => {
    if (!window.confirm('This will issue temporary passwords to drivers who have no password yet (a CSV downloads once). Continue?')) {
      return;
    }
    
    setResetAllLoading(true);
    try {
      const response = await axios.post(`${API_URL}/drivers/reset-all-passwords`);
      toast.success(`Reset passwords for ${response.data.count} drivers`);
      const creds = response.data.credentials || [];
      if (creds.length) {
        const csv = ['Driver Name,Username,Temporary Password', ...creds.map(c => `"${c.full_name}","${c.username}","${c.temporary_password}"`)].join('\n');
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        link.setAttribute('download', `driver_temp_passwords_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link); link.click(); link.remove();
      }
      fetchDrivers(); // Refresh to get new passwords
    } catch (error) {
      toast.error('Failed to reset passwords');
    } finally {
      setResetAllLoading(false);
    }
  };

  const exportCredentials = () => {
    // Create CSV content
    const headers = ['Driver Name', 'Username (Email)', 'Password', 'Status'];
    const rows = filteredDrivers.map(d => [
      d.full_name || d.driver_name || '',
      d.email,
      '(hidden - reset to issue new)',
      d.is_active ? 'Active' : 'Inactive'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `driver_credentials_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Credentials exported');
  };

  const filteredDrivers = drivers.filter(driver =>
    (driver.full_name || driver.driver_name || '').toLowerCase().includes(search.toLowerCase()) ||
    driver.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-credentials">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <KeyRound className="h-8 w-8 text-orange-500" />
              Master Credentials
            </h1>
            <p className="text-muted-foreground mt-1">View and manage driver login credentials</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={handleResetAllPasswords} 
              variant="destructive"
              disabled={resetAllLoading}
              data-testid="reset-all-passwords"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${resetAllLoading ? 'animate-spin' : ''}`} />
              {resetAllLoading ? 'Resetting...' : 'Reset All Passwords'}
            </Button>
            <Button onClick={exportCredentials} variant="outline" data-testid="export-credentials">
              <Download className="h-4 w-4 mr-2" />
              Export Credentials
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <KeyRound className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Login Credentials Help</p>
                <p className="text-sm text-blue-700">
                  Use this page to help drivers with their login details. Click the eye icon to reveal passwords, 
                  or use the reset button to generate a new password.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-credentials"
              />
            </div>
          </CardContent>
        </Card>

        {/* Credentials Table */}
        <Card>
          <CardHeader>
            <CardTitle>Driver Credentials</CardTitle>
            <CardDescription>
              {filteredDrivers.length} driver{filteredDrivers.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredDrivers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mb-2">No drivers found</h3>
                <p className="text-muted-foreground">No matching credentials</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver Name</TableHead>
                      <TableHead>Username (Email)</TableHead>
                      <TableHead>Password</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDrivers.map((driver) => (
                      <TableRow key={driver.id} data-testid={`cred-row-${driver.id}`}>
                        <TableCell className="font-medium">
                          {driver.full_name || driver.driver_name || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                              {driver.email}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(driver.email, 'Username')}
                              title="Copy username"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {driver.readable_password ? (
                              <>
                                <code className="text-sm bg-slate-100 px-2 py-1 rounded font-mono">
                                  {visiblePasswords[driver.id] ? driver.readable_password : '••••••••'}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => togglePasswordVisibility(driver.id)}
                                  title={visiblePasswords[driver.id] ? 'Hide password' : 'Show password'}
                                >
                                  {visiblePasswords[driver.id] ? (
                                    <EyeOff className="h-3 w-3" />
                                  ) : (
                                    <Eye className="h-3 w-3" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(driver.readable_password, 'Password')}
                                  title="Copy password"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </>
                            ) : (
                              <span className="text-muted-foreground text-sm italic">
                                Hidden - reset to issue a new one
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={driver.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                            {driver.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResetPassword(driver)}
                            disabled={resetLoading[driver.id]}
                            data-testid={`reset-${driver.id}`}
                          >
                            <RefreshCw className={`h-4 w-4 mr-1 ${resetLoading[driver.id] ? 'animate-spin' : ''}`} />
                            {resetLoading[driver.id] ? 'Resetting...' : 'Reset Password'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reset Success Dialog */}
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Password Reset Successful</DialogTitle>
              <DialogDescription>
                New password for {selectedDriver?.full_name || selectedDriver?.driver_name}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 mb-2">New Login Credentials:</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white rounded p-2">
                    <span className="text-sm text-muted-foreground">Username:</span>
                    <code className="font-mono">{selectedDriver?.email}</code>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded p-2">
                    <span className="text-sm text-muted-foreground">Password:</span>
                    <code className="font-mono text-lg font-bold text-green-700">{newPassword}</code>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Please share these credentials with the driver securely. They should change their password after first login.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  copyToClipboard(`Username: ${selectedDriver?.email}\nPassword: ${newPassword}`, 'Credentials');
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy All
              </Button>
              <Button onClick={() => setShowResetDialog(false)}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
